import { useEffect, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { handleGetUserMediaError } from '../utils/media';
import { setVideoError, setMicrophoneError } from '../store/slices/statusSlice';

const usePermissionRequest = (
  streamHandler,
  checkedPermission,
  setCheckedPermission
) => {
  const dispatch = useDispatch();

  const onDenied = useCallback(
    ({ useAudio, useVideo }) => {
      if (useAudio && useVideo) return;
      if (useAudio) {
        dispatch(setMicrophoneError('microphoneBlocked'));
      }
      if (useVideo) {
        dispatch(setVideoError('cameraBlocked'));
      }
    },
    [dispatch]
  );

  const onDismissed = useCallback(
    ({ useAudio, useVideo }) => {
      console.log('Permission dismissed', { useAudio, useVideo });
      if (useAudio) {
        dispatch(setMicrophoneError('microphoneDismissed'));
      }
      if (useVideo) {
        dispatch(setVideoError('cameraDismissed'));
      }
    },
    [dispatch]
  );

  const onCameraTaken = useCallback(
    ({ useAudio, useVideo }) => {
      if (useAudio && useVideo) return;
      if (useVideo) {
        dispatch(setVideoError('cameraTaken'));
      }
    },
    [dispatch]
  );

  const requestPermissions = useCallback(
    async ({ useAudio = true, useVideo = true }) => {
      let dismissed = false;
      try {
        await streamHandler.verifyPermission({
          useAudio,
          useVideo,
        });
      } catch (e) {
        handleGetUserMediaError(e, {
          onSystemRejection: () => {
            onDenied({ useAudio, useVideo });
          },
          onUserDenied: () => {
            onDenied({ useAudio, useVideo });
          },
          onUserDismissed: () => {
            dismissed = true;
            onDismissed({ useAudio, useVideo });
          },
          onCameraTaken: () => {
            onCameraTaken({ useAudio, useVideo });
          },
          catchAll: () => onDismissed({ useAudio, useVideo }),
        });

        if (useAudio && useVideo && !dismissed) {
          await requestPermissions({ useAudio: false, useVideo: true });
          await requestPermissions({ useAudio: true, useVideo: false });
          setCheckedPermission({ audio: true, video: true });
        }
      }
    },
    [streamHandler, onDenied, onDismissed, onCameraTaken, setCheckedPermission]
  );

  useEffect(() => {
    if (checkedPermission.audio || checkedPermission.video) return;

    (async () => {
      await requestPermissions({ useAudio: true, useVideo: true });
    })();
  }, [checkedPermission, requestPermissions]);
};

export default usePermissionRequest;
